import { useState, useMemo } from 'react';
import { Box, Tooltip, Button } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { FunnelData, DimensionType, DimensionRow } from '../../../../hooks/analytics';
import { SectionCard, SectionTitle, AnalyticsColors } from '../../components/analyticsUi';
import InfoTooltip from 'components/v2/common/infoTooltip';
import FunnelChart from '../../components/FunnelChart';
import { STAGE_COLORS, STAGE_LABELS } from '../../components/FunnelChart';
import DimensionToggle from '../../components/DimensionToggle';
import { formatPercentage, formatCurrency } from 'utils/formatters';
import { CONVERSION_FUNNEL } from '../../constants/metricDescriptions';

const INITIAL_VISIBLE = 10;

interface ConversionFunnelProps {
    current: FunnelData;
    comparison: FunnelData;
    dimension: DimensionType;
    onDimensionChange: (d: DimensionType) => void;
    dimensionData: DimensionRow[] | null;
    isOwner: boolean;
    onTransitionClick?: (transitionKey: string) => void;
}

const ConversionFunnel = ({
    current,
    comparison,
    dimension,
    onDimensionChange,
    dimensionData,
    isOwner,
    onTransitionClick,
}: ConversionFunnelProps) => {
    const [showAll, setShowAll] = useState(false);

    // Sort by volume (registered count) descending
    const sortedData = useMemo(() => {
        if (!dimensionData) return [];
        return [...dimensionData].sort((a, b) => b.funnel.registered - a.funnel.registered);
    }, [dimensionData]);

    const visibleData = showAll ? sortedData : sortedData.slice(0, INITIAL_VISIBLE);
    const hasMore = sortedData.length > INITIAL_VISIBLE;

    // Reset "show all" when dimension changes
    const handleDimensionChange = (d: DimensionType) => {
        setShowAll(false);
        onDimensionChange(d);
    };

    return (
        <SectionCard>
            <SectionTitle>
                Conversion Funnel
                <InfoTooltip title={CONVERSION_FUNNEL} />
            </SectionTitle>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: '1.5rem',
                    flexWrap: 'wrap',
                    gap: '1rem',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
                    <Box
                        component="span"
                        sx={{ fontSize: 28, fontWeight: 700, color: AnalyticsColors.primary }}
                    >
                        {formatPercentage(current.conversionRates.overall)}
                    </Box>
                    <Box
                        component="span"
                        sx={{ fontSize: 13, color: AnalyticsColors.textSecondary }}
                    >
                        overall conversion (this month)
                    </Box>
                </Box>
                <DimensionToggle
                    value={dimension}
                    onChange={handleDimensionChange}
                    isOwner={isOwner}
                />
            </Box>

            {dimension === 'all' && (
                <FunnelChart
                    funnel={current.funnel}
                    rates={current.conversionRates}
                    speed={current.speed}
                    comparisonRates={comparison.conversionRates}
                    comparisonSpeed={comparison.speed}
                    onTransitionClick={onTransitionClick}
                />
            )}

            {dimension !== 'all' && sortedData.length > 0 && (
                <Box sx={{ mt: '1.5rem' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: '1rem',
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                            {STAGE_LABELS.map((label, i) => (
                                <Box
                                    key={label}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                        fontSize: 11,
                                        fontWeight: 500,
                                        color: AnalyticsColors.textSecondary,
                                    }}
                                >
                                    <Box
                                        component="span"
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '2px',
                                            background: STAGE_COLORS[i],
                                        }}
                                    />
                                    {label}
                                </Box>
                            ))}
                        </Box>
                        <Box
                            component="span"
                            sx={{
                                fontSize: 12,
                                color: AnalyticsColors.textTertiary,
                                fontWeight: 500,
                            }}
                        >
                            {showAll
                                ? `${sortedData.length} items`
                                : `Top ${Math.min(INITIAL_VISIBLE, sortedData.length)} of ${sortedData.length}`}
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            border: `1px solid ${AnalyticsColors.borderLight}`,
                            borderRadius: '10px',
                            overflow: 'hidden',
                        }}
                    >
                        {visibleData.map((row, idx) => {
                            const maxVal = row.funnel.registered || 1;
                            const stages = [
                                { val: row.funnel.registered },
                                { val: row.funnel.assessmentCompleted },
                                { val: row.funnel.offerSent },
                                { val: row.funnel.paid },
                            ];
                            const even = idx % 2 === 0;

                            return (
                                <Box
                                    key={row.key}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns:
                                            'minmax(160px, 1fr) 2fr minmax(100px, auto)',
                                        alignItems: 'center',
                                        gap: '1rem',
                                        p: '0.625rem 1rem',
                                        background: even
                                            ? AnalyticsColors.surfaceBg
                                            : AnalyticsColors.cardBg,
                                        borderBottom: `1px solid ${AnalyticsColors.borderLight}`,
                                        '&:last-child': { borderBottom: 'none' },
                                        '&:hover': {
                                            background: `${AnalyticsColors.primaryLight}22`,
                                        },
                                    }}
                                >
                                    <Tooltip title={row.key} arrow placement="left">
                                        <Box
                                            sx={{
                                                fontSize: 13,
                                                fontWeight: 600,
                                                color: AnalyticsColors.textPrimary,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {row.key}
                                        </Box>
                                    </Tooltip>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: '2px',
                                            height: 22,
                                            width: '100%',
                                        }}
                                    >
                                        {stages.map((s, i) => (
                                            <Box
                                                key={i}
                                                sx={{
                                                    height: '100%',
                                                    width: `${Math.max((s.val / maxVal) * 100, 4)}%`,
                                                    background: STAGE_COLORS[i],
                                                    borderRadius: '4px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    color: 'white',
                                                    minWidth: 22,
                                                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.15)',
                                                    transition: 'width 0.4s ease',
                                                }}
                                            >
                                                {s.val}
                                            </Box>
                                        ))}
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-end',
                                            gap: '2px',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        <Box
                                            component="span"
                                            sx={{
                                                fontSize: 13,
                                                fontWeight: 700,
                                                color: AnalyticsColors.primary,
                                            }}
                                        >
                                            {formatPercentage(row.conversionRates.overall)}
                                        </Box>
                                        <Box
                                            component="span"
                                            sx={{
                                                fontSize: 11,
                                                color: AnalyticsColors.textTertiary,
                                                fontWeight: 500,
                                            }}
                                        >
                                            {row.funnel.registered} leads
                                            {row.avgFee != null &&
                                                ` · ${formatCurrency(row.avgFee)}`}
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>

                    {hasMore && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                p: '0.75rem 0 0.25rem',
                            }}
                        >
                            <Button
                                size="small"
                                onClick={() => setShowAll(!showAll)}
                                startIcon={
                                    showAll ? (
                                        <ExpandLess sx={{ fontSize: 16 }} />
                                    ) : (
                                        <ExpandMore sx={{ fontSize: 16 }} />
                                    )
                                }
                                sx={{
                                    textTransform: 'none',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    color: AnalyticsColors.primary,
                                    '&:hover': { background: AnalyticsColors.primaryLight },
                                }}
                            >
                                {showAll ? 'Show less' : `Show all ${sortedData.length} items`}
                            </Button>
                        </Box>
                    )}
                </Box>
            )}

            {dimension !== 'all' && sortedData.length === 0 && (
                <Box sx={{ textAlign: 'center', p: '2rem', color: AnalyticsColors.textTertiary }}>
                    No data for this breakdown
                </Box>
            )}
        </SectionCard>
    );
};

export default ConversionFunnel;
